import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import PaiLink from '../base-components/pai-link';
import axios from 'axios';
import config from '../../../config';

const ErrorDialog: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { firstName, lastName } = useUserData();

  const { errorOccurred, isNetworkError, handleCloseErrorDialog, errors, source, timeStamp } = useErrorStore((state) => ({
    errorOccurred: state.errorOccurred,
    isNetworkError: state.isNetworkError,
    handleCloseErrorDialog: state.handleCloseErrorDialog,
    errors: state.errors,
    source: state.source,
    timeStamp: state.timeStamp,
  }));

  const [backOnline, setBackOnline] = useState(false);
  const [secondsToNextCheck, setSecondsToNextCheck] = useState(15);
  const intervalRef = useRef<any>(null);
  const tickRef = useRef<any>(null);

  const openEmail = () => {
    const errorsMapped = !isNetworkError
      ? errors.map((error) => `${error.code} ${error.message} @${_.upperCase(error.requestType)} ${error.request ?? 'unknown'}`).join('%0d%0a')
      : t('serverErrorTitle');

    const emailBody = t('errorEmailBody', {
      source,
      errors: errorsMapped,
      timestamp: timeStamp,
      userName: `${firstName ?? ''} ${lastName ?? ''}`,
    });

    const mailtoUrl = `mailto:simon.tschuertz@secret-ai-agent.de; info@secret-ai-agent.de?subject=${encodeURIComponent(
      t('errorEmailSubject')
    )}&body=${encodeURIComponent(emailBody)}`;

    window.open(mailtoUrl, '_blank');
  };

  const checkServerAvailability = async () => {
    try {
      const url = `${_.get(config, 'apiBaseUrl')}/users/me`;
      await axios.get(url, { timeout: 5000, validateStatus: (s) => s >= 200 && s < 500, withCredentials: true });
      setBackOnline(true);
    } catch {
      // keep polling
    }
  };

  useEffect(() => {
    if (errorOccurred && isNetworkError) {
      setBackOnline(false);
      setSecondsToNextCheck(15);
      if (tickRef.current) clearInterval(tickRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);

      tickRef.current = setInterval(() => {
        setSecondsToNextCheck((s) => (s <= 1 ? 0 : s - 1));
      }, 1000);

      intervalRef.current = setInterval(() => {
        checkServerAvailability();
        setSecondsToNextCheck(15);
      }, 15000);
    } else {
      if (tickRef.current) clearInterval(tickRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [errorOccurred, isNetworkError]);

  useEffect(() => {
    if (backOnline) {
      if (tickRef.current) clearInterval(tickRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [backOnline]);

  return (
    <Dialog classes={{ paper: classes.paper }} open={errorOccurred} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={cx(classes.title, isNetworkError ? 'network-error' : undefined)}>
        <Typography className={classes.titleText}>
          {!isNetworkError ? t('errorDialogTitle') : backOnline ? t('serverBackOnlineTitle') : t('serverTemporaryTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {!isNetworkError ? (
          <Stack gap={2}>
            <Typography className={classes.text}>
              <Trans i18nKey={'errorDialogText1'} values={{ source }}>
                One or more errors have occurred on the path <span className={classes.strong}>https://app.secret-ai-agent.de/dashboard</span>
              </Trans>
            </Typography>
            <div>
              <Typography className={cx(classes.text, classes.strong)} gutterBottom={true}>
                {t('timestamp')}:
              </Typography>
              <Typography className={classes.text}>{timeStamp}</Typography>
            </div>
            <Typography className={classes.text}>{t('errorDialogText2')}</Typography>
            <Typography className={classes.text} fontWeight={'bold'}>
              {t('errorsOccurred')}
            </Typography>
            <List disablePadding={true}>
              {errors.map((error, index) => (
                <PaiListItem key={index} text={`${error.code} ${error.message}`} subText={`${_.upperCase(error.requestType)} ${error.request}`} />
              ))}
            </List>
          </Stack>
        ) : backOnline ? (
          <Stack gap={2} alignItems={'center'} textAlign={'center'}>
            <Typography className={classes.text}>{t('serverBackOnlineBody')}</Typography>
          </Stack>
        ) : (
          <Stack gap={2} alignItems={'center'} textAlign={'center'}>
            <CircularProgress size={28} />
            <Typography className={classes.text}>{t('serverTemporaryBody1')}</Typography>
            <Typography className={classes.text}>{t('serverTemporaryBody3')}</Typography>
            <Typography className={classes.text}>{t('serverTemporaryBody4')}</Typography>
            <Typography className={classes.text} sx={{ opacity: 0.7 }}>
              {t('serverTemporaryNextCheck', { seconds: secondsToNextCheck })}
            </Typography>
            <Typography className={classes.text} sx={{ opacity: 0.7 }}>
              {t('timestamp')}: {timeStamp}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions className={cx(classes.actions, isNetworkError ? classes.centerActions : undefined)}>
        {!isNetworkError ? (
          <>
            <PaiLink text={t('report')} size={'lg'} variant={'primary'} underline={'always'} ml={'4px'} mr={'4px'} action={openEmail} />
            <PaiButton text={t('ok')} size={'small'} border={'rounded'} action={handleCloseErrorDialog} />
          </>
        ) : backOnline ? (
          <PaiButton text={t('tryAgain')} border={'rounded'} size={'small'} action={() => window.location.reload()} />
        ) : (
          <PaiButton text={t('tryAgain')} border={'rounded'} size={'small'} disabled={true} />
        )}
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
  centerActions: {
    justifyContent: 'center',
  },
  strong: {
    fontWeight: 900,
  },
}));

export default ErrorDialog;
